/**
 * InspectionWorkflow - Full-screen overlay for the 3-step inspection
 * completion flow.
 *
 * Visual chrome (top bar + step indicator + content area + photo modal +
 * lightbox) mirrors WorkOrderWorkflow so the field experience is
 * consistent across both surfaces. The internals diverge:
 *
 *   1. Checklist  — Good / Needs Attention / Critical per item, optional per-item photo
 *   2. Verify     — summary of ratings + general observation photos
 *   3. Sign-Off   — Recommendations + Follow-Up (no signature — customers
 *                   only sign work orders); submit transitions status to
 *                   'observations_complete'
 *
 * The hook (useInspectionWorkflow) owns the items state, photo
 * uploads bookkeeping, and the final PATCH; this component owns the
 * overlay shell, scroll-lock, escape-to-close, and the per-item +
 * step-2 photo upload mechanics that need photoTypeService /
 * uploadService (services live above the workflow tree).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	useInspectionWorkflow,
	InspectionWorkflowStep,
	InspectionWorkflowSource,
} from '../../../hooks/useInspectionWorkflow';
import { Spinner } from '../../atoms/Spinner';
import InspectionWorkflowChecklist from './InspectionWorkflowChecklist';
import InspectionWorkflowVerify from './InspectionWorkflowVerify';
import InspectionWorkflowSignOff from './InspectionWorkflowSignOff';
import { WorkflowPhotoCaptureModal } from '../WorkflowPhotoCaptureModal';
import type { WorkflowPhotoCapturePayload } from '../WorkflowPhotoCaptureModal';
import { PhotoLightbox } from '../PhotoLightbox';
import type { CapturedPhoto } from '../PhotoCapture/PhotoCapture';
import { uploadService } from '../../../services/uploadService';
import { photoTypeService } from '../../../services/photoTypeService';
import { capturedPhotoToFile } from '../PhotoCapture';
import { usePhotoUploadQueue } from '../../../hooks/usePhotoUploadQueue';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { PhotoUploadStatusBar } from '../../molecules/PhotoUploadStatusBar';
import type { SlotUploadStatuses, PhotoSlot } from '../../molecules/ChecklistItemPhotoActions';
import type { CapturedPhotoPhases } from '../../../services/checklistService';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface InspectionWorkflowProps {
	isOpen: boolean;
	onClose: () => void;
	inspectionId: string;
	/** Inspection record (subset) — used for status-aware mounting. */
	inspection: InspectionWorkflowSource;
	/** Display title for the top bar. */
	inspectionTitle: string;
	/** Optional callback fired after the workflow successfully submits. */
	onComplete?: () => void;
	/** Optional extra classes for the overlay root */
	className?: string;
}

/* ============================================
 * STEP METADATA
 * ============================================ */

const STEPS: { key: InspectionWorkflowStep; label: string; shortLabel: string }[] = [
	{ key: 'checklist', label: 'Checklist', shortLabel: '1' },
	{ key: 'verify', label: 'Verify', shortLabel: '2' },
	{ key: 'sign-off', label: 'Sign Off', shortLabel: '3' },
];

/* ============================================
 * COMPONENT
 * ============================================ */

const InspectionWorkflow: React.FC<InspectionWorkflowProps> = ({
	isOpen,
	onClose,
	inspectionId,
	inspection,
	inspectionTitle,
	onComplete,
	className,
}) => {
	const workflow = useInspectionWorkflow(
		inspectionId,
		inspection,
		isOpen,
		onComplete,
	);

	/* Per-item photo capture state — mirrors WorkOrderWorkflow. The
	   capturingFor record carries the optional phase so the upload
	   handler knows whether the user is filling a 'before' or 'after'
	   slot (or a single-photo item, when phase is undefined). */
	const [capturingFor, setCapturingFor] = useState<
		{ itemId: string; phase?: 'before' | 'after' } | null
	>(null);
	const [capturedPhotosByItem, setCapturedPhotosByItem] = useState<
		Map<string, CapturedPhotoPhases>
	>(new Map());

	/* Lightbox state — null when closed. Lives at this level so it
	   layers above the workflow's z-[55] overlay. */
	/* Background upload queue for per-item photos — see the identical setup
	   in WorkOrderWorkflow. Saving in the capture modal enqueues and returns
	   so the inspector isn't held up by a slow upload. */
	const photoUploads = usePhotoUploadQueue<{ itemId: string; slot: PhotoSlot }>();

	/* Flatten the queue into the per-item shape the checklist consumes. */
	const uploadStatusByItem = useMemo(() => {
		const byItem = new Map<string, SlotUploadStatuses>();
		photoUploads.items.forEach((entry) => {
			const existing = byItem.get(entry.meta.itemId) ?? {};
			byItem.set(entry.meta.itemId, { ...existing, [entry.meta.slot]: entry.status });
		});
		return byItem;
	}, [photoUploads.items]);

	/** Retry every stalled upload — wired to the status bar's "Retry all". */
	const handleRetryAllUploads = useCallback(() => {
		photoUploads.items
			.filter((entry) => entry.status === 'failed' || entry.status === 'waiting')
			.forEach((entry) => photoUploads.retry(entry.id));
	}, [photoUploads]);

	const [lightbox, setLightbox] = useState<{ url: string; caption: string | null; area: string | null } | null>(null);

	/**
	 * Resolve the seeded "Observation" photo type for this company. Used
	 * for both per-item photos (Step 1) and the general observation
	 * photos uploaded on Step 2 commit. Inspections diverge from work
	 * orders here: WO defaults to "After"; inspection per-item photos
	 * naturally belong with the rest of the visit's observation set.
	 */
	const resolveObservationPhotoTypeId = async (): Promise<number | null> => {
		if (!inspection.companyId) return null;
		return photoTypeService.resolveByName(String(inspection.companyId), 'Observation');
	};

	/* Per-item photo handler — called by the WorkflowPhotoCaptureModal when
	   the user finishes capture. Enqueues and returns immediately so the modal
	   closes on the spot; the upload runs in the background with
	   checklist_item_id + the captured phase so the photo lands in the right
	   slot on the inspection_photos table. */
	const handlePerItemPhotoSave = async (payload: WorkflowPhotoCapturePayload) => {
		if (!capturingFor || !inspection.companyId) {
			throw new Error('Missing context for upload.');
		}
		const { itemId, phase } = capturingFor;
		const slot: PhotoSlot = phase ?? 'single';

		photoUploads.enqueue({
			previewUrl: payload.photo.base64,
			meta: { itemId, slot },
			upload: async () => {
				const photoTypeId = await resolveObservationPhotoTypeId();
				if (!photoTypeId) {
					throw new Error(
						'No "Observation" photo type configured for this company. Ask a Super Admin to seed it in the CMS.'
					);
				}
				const file = await capturedPhotoToFile(payload.photo);
				return uploadService.uploadInspectionPhoto(inspectionId, file, {
					photoTypeId,
					checklistItemId: Number(itemId),
					photoPhase: phase ?? null,
					area: payload.area || undefined,
					areaId: payload.areaId ?? undefined,
					title: payload.title || undefined,
					caption: payload.details || undefined,
				});
			},
			onUploaded: () => workflow.refresh(),
		});

		// Optimistic slot flip — matches WorkOrderWorkflow's pattern so the
		// camera CTA reacts immediately. Its label still tracks the real
		// upload state via uploadStatusByItem.
		setCapturedPhotosByItem((prev) => {
			const next = new Map(prev);
			const existing = next.get(itemId) ?? {};
			next.set(itemId, { ...existing, [slot as keyof CapturedPhotoPhases]: true });
			return next;
		});
	};

	/* Step-2 photo uploader — passed to the hook so it can run them in
	   sequence and surface a clear error if any individual upload
	   fails. No checklistItemId here; these are general visit photos. */
	const uploadStep2Photo = async (photo: CapturedPhoto) => {
		const photoTypeId = await resolveObservationPhotoTypeId();
		if (!photoTypeId) {
			throw new Error('No "Observation" photo type configured for this company.');
		}
		const file = await capturedPhotoToFile(photo);
		await uploadService.uploadInspectionPhoto(inspectionId, file, {
			photoTypeId,
			caption: photo.fileName,
		});
	};

	/* Lock background scroll while the overlay is open (shared,
	   stack-safe lock — same as WorkOrderWorkflow). */
	useBodyScrollLock(isOpen);

	/* Close on Escape. */
	useEffect(() => {
		if (!isOpen) return;
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	const currentStepIndex = STEPS.findIndex((s) => s.key === workflow.currentStep);

	/* z-[55]: above the phone BottomTabBar (z-50), which used to draw over
	   the checklist's sticky Continue button, and below every Modal (z-[60]+)
	   opened from inside the workflow. */
	return (
		<div
			className={`inspection-workflow fixed inset-0 z-[55] flex flex-col bg-white ${className ?? ''}`}
			data-ref="organism-inspection-workflow"
			role="dialog"
			aria-modal="true"
			aria-label={`Inspection Workflow: ${inspectionTitle}`}
		>
			{/* ============================================
			 * TOP BAR — Close button, title, step indicator
			 * ============================================ */}
			<div className="inspection-workflow__topbar flex-shrink-0 bg-white border-b border-gray-200">
				<div className="flex items-center justify-between px-16px pt-12px pb-8px">
					<button type="button" onClick={onClose} className="w-44px h-44px rounded-full flex items-center justify-center text-brand-text-muted hover:text-brand-text hover:bg-gray-100 transition-colors -ml-8px" aria-label="Close workflow">
						<svg className="w-24px h-24px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
							<path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
						</svg>
					</button>

					<h2 className="text-16px font-sans font-bold text-brand-text truncate flex-1 text-center mx-8px">
						{inspectionTitle}
					</h2>

					<div className="w-44px" />
				</div>

				<div className="flex items-center justify-center gap-4px px-16px pb-12px">
					{STEPS.map((step, index) => {
						const isCompleted = index < currentStepIndex;
						const isActive = index === currentStepIndex;
						return (
							<React.Fragment key={step.key}>
								{index > 0 && (
									<div className={['flex-1 h-2px max-w-[60px] rounded-full transition-colors', isCompleted || isActive ? 'bg-brand-primary' : 'bg-gray-200'].join(' ')} />
								)}
								<div className="flex flex-col items-center gap-4px">
									<div className={['w-32px h-32px rounded-full flex items-center justify-center text-13px font-sans font-bold transition-colors', isActive ? 'bg-brand-primary text-white shadow-sm' : isCompleted ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500'].join(' ')}>
										{isCompleted ? (
											<svg className="w-16px h-16px" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" aria-hidden="true">
												<path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
											</svg>
										) : (
											step.shortLabel
										)}
									</div>
									<span className={['text-11px font-sans font-medium', isActive ? 'text-brand-primary' : isCompleted ? 'text-green-600' : 'text-gray-400'].join(' ')}>
										{step.label}
									</span>
								</div>
							</React.Fragment>
						);
					})}
				</div>
			</div>

			{/* Background photo uploads — one line under the step bar so the
			 * inspector can see whether their photos have landed. Renders
			 * nothing when the queue is empty. */}
			<PhotoUploadStatusBar items={photoUploads.items} onRetryAll={handleRetryAllUploads} />

			{/* ============================================
			 * STEP CONTENT AREA
			 * ============================================ */}
			<div className="inspection-workflow__body flex-1 overflow-hidden flex flex-col">
				{workflow.isLoading ? (
					<div className="flex-1 flex flex-col items-center justify-center gap-12px">
						<Spinner size="lg" />
						<p className="text-15px font-sans text-brand-text-muted">
							Loading checklist...
						</p>
					</div>
				) : (
					<>
						{workflow.currentStep === 'checklist' && (
							<InspectionWorkflowChecklist
								items={workflow.items}
								onRateItem={workflow.rateItem}
								/* Rename / add on the fly — scoped to this
								   inspection's checklist, not the template. */
								onRenameItem={workflow.renameItem}
								onAddItem={workflow.addItem}
								allItemsRated={workflow.allItemsRated}
								onContinue={() => workflow.goToStep('verify')}
								error={workflow.error}
								onCapturePhotoForItem={
									inspection.companyId
										? (itemId, phase) => setCapturingFor({ itemId, phase })
										: undefined
								}
								capturedPhotosByItem={capturedPhotosByItem}
								uploadStatusByItem={uploadStatusByItem}
								onExpandPhoto={(url, meta) => setLightbox({ url, ...meta })}
							/>
						)}

						{workflow.currentStep === 'verify' && (
							<InspectionWorkflowVerify
								items={workflow.items}
								verificationPhotos={workflow.verificationPhotos}
								onAddPhoto={workflow.addVerificationPhoto}
								onRemovePhoto={workflow.removeVerificationPhoto}
								onSubmitVerification={() => workflow.submitVerification(uploadStep2Photo)}
								onBack={() => workflow.goToStep('checklist')}
								isSubmitting={workflow.isSubmitting}
								error={workflow.error}
							/>
						)}

						{workflow.currentStep === 'sign-off' && (
							<InspectionWorkflowSignOff
								initialRecommendationsRemarks={inspection.recommendationsRemarks ?? ''}
								initialFollowUpNotes={inspection.followUpNotes ?? ''}
								isSubmitted={workflow.isSubmitted}
								submittedByName={null}
								submittedAt={null}
								onSubmitSignOff={workflow.submitSignOff}
								onBack={() => workflow.goToStep('verify')}
								isSubmitting={workflow.isSubmitting}
								error={workflow.error}
							/>
						)}
					</>
				)}
			</div>

			{/* Per-item photo capture modal (Step 1 camera CTA) */}
			<WorkflowPhotoCaptureModal
				isOpen={capturingFor !== null}
				onClose={() => setCapturingFor(null)}
				onSave={handlePerItemPhotoSave}
				title={
					capturingFor
						? `Photo for: ${workflow.items.find((i) => i.id === capturingFor.itemId)?.title ?? 'item'}${capturingFor.phase ? ` — ${capturingFor.phase.toUpperCase()}` : ''}`
						: 'Add photo'
				}
			/>

			{/* Fullscreen photo viewer */}
			<PhotoLightbox
				isOpen={lightbox !== null}
				imageUrl={lightbox?.url ?? null}
				caption={lightbox?.caption ?? null}
				area={lightbox?.area ?? null}
				onClose={() => setLightbox(null)}
			/>
		</div>
	);
};

export default InspectionWorkflow;
